/**
 * 画布字体与字重配置。
 *
 * 这里是字体元数据的单一来源；新增/调整字体时必须同步
 * `shared/styles/fonts.css` 中实际注册的 @font-face font-weight。
 */

export type DesignerFontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

export interface DesignerFontOption {
  value: string;
  label: string;
  /** 与 shared/styles/fonts.css 中 @font-face 注册的 font-weight 保持一致。 */
  weights: readonly DesignerFontWeight[];
  defaultWeight: DesignerFontWeight;
  /** 字体预览和 FontFaceSet.load 使用的代表字重。 */
  previewWeight?: DesignerFontWeight;
}

export const DESIGNER_FONT_WEIGHT_LABELS: Record<DesignerFontWeight, string> = {
  100: '100 极细',
  200: '200 纤细',
  300: '300 细',
  400: '400 常规',
  500: '500 中等',
  600: '600 半粗',
  700: '700 粗',
  800: '800 特粗',
  900: '900 黑体',
};

export const DESIGNER_FONT_ALL_WEIGHTS: readonly DesignerFontWeight[] = [
  100,
  200,
  300,
  400,
  500,
  600,
  700,
  800,
  900,
];

/** 设计器可用字体列表。 */
export const DESIGNER_FONT_OPTIONS: readonly DesignerFontOption[] = [
  {
    value: 'system-ui',
    label: '系统默认',
    weights: [400, 700],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'AlibabaPuHuiTi3',
    label: '阿里巴巴普惠体 3.0',
    weights: [100, 300, 400, 500, 600, 700, 800, 900],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'NotoSerifSC',
    label: '思源宋体',
    weights: [400, 700],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'ZhuqueFangsong',
    label: '朱雀仿宋',
    weights: [400],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'XiawuWenkai',
    label: '霞鹜文楷',
    weights: [300, 400, 500],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'AlimamaShuHeiTi',
    label: '阿里妈妈数黑体',
    weights: [700],
    defaultWeight: 700,
    previewWeight: 700,
  },
  {
    value: 'TaoBaoMaiCaiTi',
    label: '淘宝买菜体',
    weights: [400],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'AlimamaFangYuanTiVF',
    label: '阿里妈妈方圆体',
    weights: [200, 300, 400, 500, 600, 700],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'DouyinMeiHaoTi',
    label: '抖音美好体',
    weights: [400],
    defaultWeight: 400,
    previewWeight: 700,
  },
  {
    value: 'D-DIN-PRO',
    label: '英文常规体 D-DIN',
    weights: [400, 500, 600, 700, 800, 900],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'GeneralSans',
    label: '英文常规体 GeneralSans',
    weights: [200, 300, 400, 500, 600, 700],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'HoperBegin',
    label: '英文签名体 HoperBegin',
    weights: [400],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'StylishMother',
    label: '英文艺术体 StylishMother',
    weights: [400],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'Roglogo',
    label: '英文艺术体 Roglogo',
    weights: [700],
    defaultWeight: 400,
    previewWeight: 400,
  },
  {
    value: 'Monoton',
    label: '英文艺术体 Monoton',
    weights: [400],
    defaultWeight: 400,
    previewWeight: 400,
  },
];

/** 默认字体 */
export const DEFAULT_DESIGNER_FONT = 'system-ui';

export function getDesignerFontOption(fontFamily: string | undefined): DesignerFontOption {
  return DESIGNER_FONT_OPTIONS.find((option) => option.value === fontFamily)
    ?? DESIGNER_FONT_OPTIONS[0];
}

export function normalizeFontWeightNumber(fontWeight: number | string | undefined): number {
  if (fontWeight === 'bold') return 700;
  if (fontWeight === 'normal' || fontWeight == null) return 400;
  const n = Number(fontWeight);
  return Number.isFinite(n) ? n : 400;
}

export function resolveSupportedFontWeight(
  fontFamily: string | undefined,
  fontWeight: number | string | undefined,
): DesignerFontWeight {
  const option = getDesignerFontOption(fontFamily);
  const requested = normalizeFontWeightNumber(fontWeight);
  const exact = option.weights.find((weight) => weight === requested);
  if (exact) return exact;
  return option.weights.reduce((best, weight) => {
    const bestDistance = Math.abs(best - requested);
    const distance = Math.abs(weight - requested);
    if (distance < bestDistance) return weight;
    if (distance === bestDistance && weight > best) return weight;
    return best;
  }, option.defaultWeight);
}

export function resolveRegularFontWeight(fontFamily: string | undefined): DesignerFontWeight {
  return resolveSupportedFontWeight(fontFamily, 400);
}

export function resolveBoldFontWeight(fontFamily: string | undefined): DesignerFontWeight {
  const option = getDesignerFontOption(fontFamily);
  return option.weights.find((weight) => weight >= 700)
    ?? option.weights[option.weights.length - 1]
    ?? option.defaultWeight;
}

export function fontLoadSpec(fontFamily: string, fontWeight?: number | string): string {
  const option = getDesignerFontOption(fontFamily);
  const weight = fontWeight ?? option.previewWeight ?? option.defaultWeight;
  return `${weight} 16px "${option.value}"`;
}
